import { useState, useEffect } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Progress } from "@/components/ui/progress";
import { DollarSign, Clock, FileText, Plus, Trash2, CheckCircle, AlertCircle, Calendar, TrendingUp, MessageSquare, Settings, Pencil } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { useNavigate } from "react-router-dom";
import { format, startOfDay, endOfDay, differenceInDays, startOfWeek, endOfWeek, addDays } from "date-fns";

interface PayrollEntry {
  id: string;
  user_id: string;
  period_start: string;
  period_end: string;
  amount: number;
  status: string;
  notes: string | null;
  created_at: string;
  galleries_count: number;
  blog_posts_count: number;
  total_posts: number;
  expected_posts: number;
  base_amount: number;
  adjustments_amount: number;
  performance_percentage: number;
  user_email?: string;
  user_name?: string;
}

interface Editor {
  id: string;
  email: string;
  full_name: string | null;
}

interface EditorRate {
  id: string;
  user_id: string;
  rate_per_post: number;
  posts_per_hour: number;
  posts_per_day: number;
  biweekly_base_pay: number;
}

interface PayrollAdjustment {
  id: string;
  payroll_id: string | null;
  user_id: string;
  adjustment_type: string;
  post_credit: number;
  amount: number;
  notes: string | null;
  adjustment_date: string | null;
  created_at: string;
}

interface PayrollDispute {
  id: string;
  payroll_id: string;
  user_id: string;
  dispute_reason: string;
  description: string | null;
  requested_adjustment: number | null;
  status: string;
  admin_response: string | null;
  created_at: string;
}

export function PayrollManager() {
  const { isAdmin, user } = useAuth();
  const navigate = useNavigate();
  const [payrollEntries, setPayrollEntries] = useState<PayrollEntry[]>([]);
  const [editors, setEditors] = useState<Editor[]>([]);
  const [editorRates, setEditorRates] = useState<EditorRate[]>([]);
  const [adjustments, setAdjustments] = useState<PayrollAdjustment[]>([]);
  const [disputes, setDisputes] = useState<PayrollDispute[]>([]);
  const [loading, setLoading] = useState(true);
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [isTimeCreditDialogOpen, setIsTimeCreditDialogOpen] = useState(false);
  const [isDisputeDialogOpen, setIsDisputeDialogOpen] = useState(false);
  const [isRatesDialogOpen, setIsRatesDialogOpen] = useState(false);
  const [isAdjustmentsDialogOpen, setIsAdjustmentsDialogOpen] = useState(false);
  const [isAdjustmentDisputeDialogOpen, setIsAdjustmentDisputeDialogOpen] = useState(false);
  const [selectedPayrollId, setSelectedPayrollId] = useState<string | null>(null);
  const [selectedAdjustmentId, setSelectedAdjustmentId] = useState<string | null>(null);
  const [adjustmentDisputeNote, setAdjustmentDisputeNote] = useState("");
  const [selectedRateEditor, setSelectedRateEditor] = useState<string | null>(null);
  const [filterEditor, setFilterEditor] = useState<string>("all");
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Editor rate form
  const [rateForm, setRateForm] = useState({
    rate_per_post: "5.71",
    posts_per_hour: "2",
    posts_per_day: "14",
    biweekly_base_pay: "800",
  });

  // Current period stats for editor view
  const [currentPeriodStats, setCurrentPeriodStats] = useState({
    galleries: 0,
    blogs: 0,
    total: 0,
    goal: 140,
    projectedPay: 0,
  });

  // Generate payroll form
  const [generateForm, setGenerateForm] = useState({
    user_id: "",
    period_start: "",
    period_end: "",
  });

  // Time credit form
  const [timeCreditForm, setTimeCreditForm] = useState({
    adjustment_type: "hour" as "hour" | "day" | "custom",
    custom_posts: "",
    notes: "",
    adjustment_date: format(new Date(), "yyyy-MM-dd"),
  });

  // Dispute form
  const [disputeForm, setDisputeForm] = useState({
    dispute_reason: "",
    description: "",
    requested_adjustment: "",
  });

  useEffect(() => {
    fetchPayrollData();
  }, []);

  const fetchPayrollData = async () => {
    try {
      // Fetch payroll entries
      const { data: payroll, error: payrollError } = await supabase
        .from("payroll")
        .select("*")
        .order("created_at", { ascending: false });

      if (payrollError) throw payrollError;

      // Fetch editors (users with editor role)
      const { data: roles, error: rolesError } = await supabase
        .from("user_roles")
        .select("user_id")
        .eq("role", "editor");

      if (rolesError) throw rolesError;

      const editorIds = roles?.map((r) => r.user_id) || [];

      // Fetch profiles for editors
      const { data: profiles, error: profilesError } = await supabase
        .from("profiles")
        .select("*");

      if (profilesError) throw profilesError;

      const editorProfiles: Editor[] = (profiles || [])
        .filter((p) => editorIds.includes(p.id))
        .map((p) => ({
          id: p.id,
          email: p.email || "",
          full_name: p.full_name,
        }));

      setEditors(editorProfiles);

      // Fetch editor rates
      const { data: rates } = await supabase.from("editor_rates").select("*");
      setEditorRates((rates as EditorRate[]) || []);

      // Fetch adjustments
      const { data: adj } = await supabase.from("payroll_adjustments").select("*").order("created_at", { ascending: false });
      setAdjustments((adj as PayrollAdjustment[]) || []);

      // Fetch disputes
      const { data: disp } = await supabase.from("payroll_disputes").select("*").order("created_at", { ascending: false });
      setDisputes((disp as PayrollDispute[]) || []);

      // Enrich payroll entries with user info
      const enrichedPayroll: PayrollEntry[] = (payroll || []).map((entry) => {
        const profile = profiles?.find((p) => p.id === entry.user_id);
        return {
          ...entry,
          galleries_count: entry.galleries_count || 0,
          blog_posts_count: entry.blog_posts_count || 0,
          total_posts: entry.total_posts || 0,
          expected_posts: entry.expected_posts || 140,
          base_amount: entry.base_amount || 0,
          adjustments_amount: entry.adjustments_amount || 0,
          performance_percentage: entry.performance_percentage || 0,
          user_email: profile?.email || "Unknown",
          user_name: profile?.full_name || null,
        };
      });

      setPayrollEntries(enrichedPayroll);

      // Calculate current period stats for editor
      if (!isAdmin && user) {
        await calculateCurrentPeriodStats(user.id, rates as EditorRate[]);
      }
    } catch (error) {
      console.error("Error fetching payroll data:", error);
      toast.error("Failed to load payroll data");
    } finally {
      setLoading(false);
    }
  };

  const calculateCurrentPeriodStats = async (userId: string, rates: EditorRate[]) => {
    // Get current bi-weekly period (starting Monday)
    const today = new Date();
    const periodStart = startOfWeek(today, { weekStartsOn: 1 });
    const periodEnd = addDays(periodStart, 13);

    // Count galleries created in this period
    const { count: galleriesCount } = await supabase
      .from("galleries")
      .select("*", { count: "exact", head: true })
      .eq("created_by", userId)
      .gte("created_at", startOfDay(periodStart).toISOString())
      .lte("created_at", endOfDay(periodEnd).toISOString());

    // Count blog posts created in this period
    const { count: blogsCount } = await supabase
      .from("blog_posts")
      .select("*", { count: "exact", head: true })
      .eq("created_by", userId)
      .gte("created_at", startOfDay(periodStart).toISOString())
      .lte("created_at", endOfDay(periodEnd).toISOString());

    const rate = rates?.find((r) => r.user_id === userId);
    const ratePerPost = rate?.rate_per_post || 5.71;
    const total = (galleriesCount || 0) + (blogsCount || 0);

    setCurrentPeriodStats({
      galleries: galleriesCount || 0,
      blogs: blogsCount || 0,
      total,
      goal: 140,
      projectedPay: total * ratePerPost,
    });
  };

  const generatePayroll = async () => {
    if (!generateForm.user_id || !generateForm.period_start || !generateForm.period_end) {
      toast.error("Please fill in all required fields");
      return;
    }

    setIsSubmitting(true);
    try {
      const periodStart = new Date(generateForm.period_start);
      const periodEnd = new Date(generateForm.period_end);

      // Count galleries created in this period
      const { count: galleriesCount } = await supabase
        .from("galleries")
        .select("*", { count: "exact", head: true })
        .eq("created_by", generateForm.user_id)
        .gte("created_at", startOfDay(periodStart).toISOString())
        .lte("created_at", endOfDay(periodEnd).toISOString());

      // Count blog posts created in this period
      const { count: blogsCount } = await supabase
        .from("blog_posts")
        .select("*", { count: "exact", head: true })
        .eq("created_by", generateForm.user_id)
        .gte("created_at", startOfDay(periodStart).toISOString())
        .lte("created_at", endOfDay(periodEnd).toISOString());

      // Get editor rate
      const rate = editorRates.find((r) => r.user_id === generateForm.user_id);
      const ratePerPost = rate?.rate_per_post || 5.71;
      const postsPerDay = rate?.posts_per_day || 14;

      // Calculate expected posts (working days only - Mon-Fri)
      const workingDays = Math.ceil(differenceInDays(periodEnd, periodStart) * 5 / 7);
      const expectedPosts = workingDays * postsPerDay;

      const totalPosts = (galleriesCount || 0) + (blogsCount || 0);
      const baseAmount = totalPosts * ratePerPost;
      const performancePercentage = expectedPosts > 0 ? Math.round((totalPosts / expectedPosts) * 100) : 0;

      // Get adjustments for this user in this period
      const { data: periodAdjustments } = await supabase
        .from("payroll_adjustments")
        .select("*")
        .eq("user_id", generateForm.user_id)
        .is("payroll_id", null);

      const adjustmentsAmount = (periodAdjustments || []).reduce((sum, adj) => sum + (adj.amount || 0), 0);
      const totalAmount = baseAmount + adjustmentsAmount;

      // Get editor name
      const editor = editors.find((e) => e.id === generateForm.user_id);

      const { data: newPayroll, error } = await supabase.from("payroll").insert({
        user_id: generateForm.user_id,
        period_start: generateForm.period_start,
        period_end: generateForm.period_end,
        amount: totalAmount,
        galleries_count: galleriesCount || 0,
        blog_posts_count: blogsCount || 0,
        total_posts: totalPosts,
        expected_posts: expectedPosts,
        base_amount: baseAmount,
        adjustments_amount: adjustmentsAmount,
        performance_percentage: performancePercentage,
        status: "pending",
        editor_name: editor?.full_name || editor?.email,
        notes: `Auto-generated: ${galleriesCount} galleries, ${blogsCount} blog posts`,
      }).select().single();

      if (error) throw error;

      // Link adjustments to this payroll
      if (periodAdjustments && periodAdjustments.length > 0 && newPayroll) {
        await supabase
          .from("payroll_adjustments")
          .update({ payroll_id: newPayroll.id })
          .in("id", periodAdjustments.map((a) => a.id));
      }

      toast.success("Payroll generated successfully");
      setIsAddDialogOpen(false);
      setGenerateForm({ user_id: "", period_start: "", period_end: "" });
      fetchPayrollData();
    } catch (error: any) {
      console.error("Error generating payroll:", error);
      toast.error(error.message || "Failed to generate payroll");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleAddTimeCredit = async () => {
    if (!timeCreditForm.notes) {
      toast.error("Please add a note explaining this adjustment");
      return;
    }

    const selectedEntry = payrollEntries.find((e) => e.id === selectedPayrollId);
    if (!selectedEntry) {
      toast.error("No payroll entry selected");
      return;
    }

    setIsSubmitting(true);
    try {
      const rate = editorRates.find((r) => r.user_id === selectedEntry.user_id);
      const ratePerPost = rate?.rate_per_post || 5.71;

      let postCredit = 0;
      switch (timeCreditForm.adjustment_type) {
        case "hour":
          postCredit = 2;
          break;
        case "day":
          postCredit = 14;
          break;
        case "custom":
          postCredit = parseInt(timeCreditForm.custom_posts) || 0;
          break;
      }

      const amount = postCredit * ratePerPost;

      // Insert adjustment
      const { error: adjError } = await supabase.from("payroll_adjustments").insert({
        payroll_id: selectedPayrollId,
        user_id: selectedEntry.user_id,
        adjustment_type: timeCreditForm.adjustment_type,
        post_credit: postCredit,
        amount,
        notes: timeCreditForm.notes,
        adjustment_date: timeCreditForm.adjustment_date,
        created_by: user?.id,
      });

      if (adjError) throw adjError;

      // Update payroll totals
      const newAdjustmentsAmount = selectedEntry.adjustments_amount + amount;
      const newAmount = selectedEntry.base_amount + newAdjustmentsAmount;

      const { error: payrollError } = await supabase
        .from("payroll")
        .update({
          adjustments_amount: newAdjustmentsAmount,
          amount: newAmount,
          updated_at: new Date().toISOString(),
        })
        .eq("id", selectedPayrollId);

      if (payrollError) throw payrollError;

      toast.success(`Added ${postCredit} post credit ($${amount.toFixed(2)})`);
      setIsTimeCreditDialogOpen(false);
      setTimeCreditForm({ adjustment_type: "hour", custom_posts: "", notes: "", adjustment_date: format(new Date(), "yyyy-MM-dd") });
      fetchPayrollData();
    } catch (error: any) {
      console.error("Error adding time credit:", error);
      toast.error(error.message || "Failed to add time credit");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSubmitDispute = async () => {
    if (!disputeForm.dispute_reason) {
      toast.error("Please select a reason for your dispute");
      return;
    }

    setIsSubmitting(true);
    try {
      const { error } = await supabase.from("payroll_disputes").insert({
        payroll_id: selectedPayrollId,
        user_id: user?.id,
        dispute_reason: disputeForm.dispute_reason,
        description: disputeForm.description || null,
        requested_adjustment: disputeForm.requested_adjustment ? parseFloat(disputeForm.requested_adjustment) : null,
      });

      if (error) throw error;

      toast.success("Dispute submitted successfully");
      setIsDisputeDialogOpen(false);
      setDisputeForm({ dispute_reason: "", description: "", requested_adjustment: "" });
      fetchPayrollData();
    } catch (error: any) {
      console.error("Error submitting dispute:", error);
      toast.error(error.message || "Failed to submit dispute");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleUpdateStatus = async (id: string, newStatus: string) => {
    try {
      const { error } = await supabase
        .from("payroll")
        .update({ status: newStatus, updated_at: new Date().toISOString() })
        .eq("id", id);

      if (error) throw error;

      toast.success(`Payment marked as ${newStatus}`);
      fetchPayrollData();
    } catch (error: any) {
      console.error("Error updating status:", error);
      toast.error("Failed to update status");
    }
  };

  const handleResolveDispute = async (disputeId: string, status: "approved" | "rejected", response: string) => {
    try {
      const dispute = disputes.find(d => d.id === disputeId);
      
      const { error } = await supabase
        .from("payroll_disputes")
        .update({
          status,
          admin_response: response,
          resolved_at: new Date().toISOString(),
          resolved_by: user?.id,
        })
        .eq("id", disputeId);

      if (error) throw error;

      // Send notification to editor
      if (dispute) {
        try {
          const { data: editorProfile } = await supabase
            .from("profiles")
            .select("email, full_name")
            .eq("id", dispute.user_id)
            .single();

          if (editorProfile?.email) {
            await supabase.functions.invoke("send-payroll-notification", {
              body: {
                type: "dispute_updated",
                recipientEmail: editorProfile.email,
                recipientName: editorProfile.full_name || "Team Member",
                data: {
                  disputeStatus: status,
                  adminResponse: response,
                },
              },
            });
          }
        } catch (notifError) {
          console.error("Failed to send dispute update notification:", notifError);
        }
      }

      toast.success(`Dispute ${status}`);
      fetchPayrollData();
    } catch (error: any) {
      console.error("Error resolving dispute:", error);
      toast.error("Failed to resolve dispute");
    }
  };

  const handleDeleteEntry = async (id: string) => {
    if (!confirm("Are you sure you want to delete this payroll entry?")) {
      return;
    }

    try {
      const { error } = await supabase.from("payroll").delete().eq("id", id);

      if (error) throw error;

      toast.success("Payroll entry deleted");
      fetchPayrollData();
    } catch (error: any) {
      console.error("Error deleting entry:", error);
      toast.error("Failed to delete entry");
    }
  };

  const openRateDialog = (editorId: string) => {
    setSelectedRateEditor(editorId);
    const existingRate = editorRates.find((r) => r.user_id === editorId);
    if (existingRate) {
      setRateForm({
        rate_per_post: existingRate.rate_per_post.toString(),
        posts_per_hour: existingRate.posts_per_hour.toString(),
        posts_per_day: existingRate.posts_per_day.toString(),
        biweekly_base_pay: existingRate.biweekly_base_pay.toString(),
      });
    } else {
      setRateForm({
        rate_per_post: "5.71",
        posts_per_hour: "2",
        posts_per_day: "14",
        biweekly_base_pay: "800",
      });
    }
    setIsRatesDialogOpen(true);
  };

  const handleSaveRate = async () => {
    if (!selectedRateEditor) return;

    setIsSubmitting(true);
    try {
      const rateData = {
        user_id: selectedRateEditor,
        rate_per_post: parseFloat(rateForm.rate_per_post) || 5.71,
        posts_per_hour: parseInt(rateForm.posts_per_hour) || 2,
        posts_per_day: parseInt(rateForm.posts_per_day) || 14,
        biweekly_base_pay: parseFloat(rateForm.biweekly_base_pay) || 800,
      };

      const existingRate = editorRates.find((r) => r.user_id === selectedRateEditor);
      
      if (existingRate) {
        const { error } = await supabase
          .from("editor_rates")
          .update({ ...rateData, updated_at: new Date().toISOString() })
          .eq("id", existingRate.id);
        if (error) throw error;
      } else {
        const { error } = await supabase
          .from("editor_rates")
          .insert(rateData);
        if (error) throw error;
      }

      toast.success("Editor rate saved successfully");
      setIsRatesDialogOpen(false);
      fetchPayrollData();
    } catch (error: any) {
      console.error("Error saving rate:", error);
      toast.error(error.message || "Failed to save rate");
    } finally {
      setIsSubmitting(false);
    }
  };

  const filteredEntries = isAdmin
    ? filterEditor === "all"
      ? payrollEntries
      : payrollEntries.filter((e) => e.user_id === filterEditor)
    : payrollEntries.filter((e) => e.user_id === user?.id);

  const pendingDisputes = disputes.filter((d) => d.status === "pending");

  const stats = {
    pending: payrollEntries
      .filter((e) => e.status === "pending")
      .reduce((sum, e) => sum + (e.amount || 0), 0),
    paidThisMonth: payrollEntries
      .filter((e) => {
        const entryDate = new Date(e.created_at);
        const now = new Date();
        return (
          e.status === "paid" &&
          entryDate.getMonth() === now.getMonth() &&
          entryDate.getFullYear() === now.getFullYear()
        );
      })
      .reduce((sum, e) => sum + (e.amount || 0), 0),
    pendingDisputes: pendingDisputes.length,
  };

  // Editor-specific stats (filtered by current user)
  const editorStats = {
    pending: payrollEntries
      .filter((e) => e.user_id === user?.id && e.status === "pending")
      .reduce((sum, e) => sum + (e.amount || 0), 0),
    paidThisMonth: payrollEntries
      .filter((e) => {
        const entryDate = new Date(e.created_at);
        const now = new Date();
        return (
          e.user_id === user?.id &&
          e.status === "paid" &&
          entryDate.getMonth() === now.getMonth() &&
          entryDate.getFullYear() === now.getFullYear()
        );
      })
      .reduce((sum, e) => sum + (e.amount || 0), 0),
    totalEarnings: payrollEntries
      .filter((e) => e.user_id === user?.id && e.status === "paid")
      .reduce((sum, e) => sum + (e.amount || 0), 0),
    pendingDisputes: disputes.filter((d) => d.user_id === user?.id && d.status === "pending").length,
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case "paid":
        return <Badge className="bg-green-100 text-green-700 border-green-200"><CheckCircle className="w-3 h-3 mr-1" />Paid</Badge>;
      case "pending":
        return <Badge className="bg-yellow-100 text-yellow-700 border-yellow-200"><Clock className="w-3 h-3 mr-1" />Pending</Badge>;
      default:
        return <Badge variant="secondary">{status}</Badge>;
    }
  };

  const getPerformanceBadge = (percentage: number) => {
    if (percentage >= 100) {
      return <Badge className="bg-green-100 text-green-700">{percentage}%</Badge>;
    } else if (percentage >= 75) {
      return <Badge className="bg-yellow-100 text-yellow-700">{percentage}%</Badge>;
    } else {
      return <Badge className="bg-red-100 text-red-700">{percentage}%</Badge>;
    }
  };

  if (loading) {
    return (
      <div className="flex h-32 items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
      </div>
    );
  }

  // Editor View
  if (!isAdmin) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="font-serif text-2xl font-semibold">My Payroll</h2>
            <p className="text-muted-foreground">Track your earnings and submit corrections</p>
          </div>
        </div>

        {/* Current Period Stats */}
        <div className="grid gap-6 md:grid-cols-3">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium">Current Period</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{currentPeriodStats.total} / {currentPeriodStats.goal}</div>
              <div className="flex gap-2 mt-1 text-xs text-muted-foreground">
                <span>{currentPeriodStats.galleries} galleries</span>
                <span>•</span>
                <span>{currentPeriodStats.blogs} blogs</span>
              </div>
              <Progress 
                value={Math.min((currentPeriodStats.total / currentPeriodStats.goal) * 100, 100)} 
                className="mt-3 h-2"
              />
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium">Projected Pay</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">${currentPeriodStats.projectedPay.toFixed(2)}</div>
              <p className="text-xs text-muted-foreground mt-1">Based on current posts</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium">Performance</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                {currentPeriodStats.goal > 0 
                  ? Math.round((currentPeriodStats.total / currentPeriodStats.goal) * 100) 
                  : 0}%
              </div>
              <p className="text-xs text-muted-foreground mt-1">of bi-weekly goal</p>
            </CardContent>
          </Card>
        </div>

        {/* Payment Summary */}
        <div className="grid gap-6 md:grid-cols-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Pending Payments</CardTitle>
              <Clock className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">${editorStats.pending.toFixed(2)}</div>
              <p className="text-xs text-muted-foreground">Awaiting payment</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Paid This Month</CardTitle>
              <DollarSign className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">${editorStats.paidThisMonth.toFixed(2)}</div>
              <p className="text-xs text-muted-foreground">{new Date().toLocaleString("default", { month: "long" })}</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Total Earnings</CardTitle>
              <TrendingUp className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">${editorStats.totalEarnings.toFixed(2)}</div>
              <p className="text-xs text-muted-foreground">All time</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Open Disputes</CardTitle>
              <AlertCircle className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{editorStats.pendingDisputes}</div>
              <p className="text-xs text-muted-foreground">Awaiting review</p>
            </CardContent>
          </Card>
        </div>

        {/* Payroll History */}
        <Card>
          <CardHeader>
            <CardTitle>Payroll History</CardTitle>
            <CardDescription>Your payment records and status</CardDescription>
          </CardHeader>
          <CardContent>
            {filteredEntries.length === 0 ? (
              <div className="flex h-48 items-center justify-center rounded-lg border border-dashed border-border">
                <div className="text-center">
                  <DollarSign className="mx-auto h-12 w-12 text-muted-foreground/50" />
                  <p className="mt-4 text-muted-foreground">No payroll entries yet</p>
                </div>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Period</TableHead>
                    <TableHead>Galleries</TableHead>
                    <TableHead>Blogs</TableHead>
                    <TableHead>Total</TableHead>
                    <TableHead>Goal</TableHead>
                    <TableHead>Pay</TableHead>
                    <TableHead>Adjustments</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredEntries.map((entry) => {
                    const hasDispute = disputes.some((d) => d.payroll_id === entry.id);
                    const entryAdjustments = adjustments.filter((a) => a.payroll_id === entry.id);
                    return (
                      <TableRow 
                        key={entry.id}
                        className="cursor-pointer hover:bg-muted/50"
                        onClick={() => navigate(`/payroll/${entry.id}`)}
                      >
                        <TableCell>
                          {format(new Date(entry.period_start), "MMM d")} - {format(new Date(entry.period_end), "MMM d, yyyy")}
                        </TableCell>
                        <TableCell>{entry.galleries_count}</TableCell>
                        <TableCell>{entry.blog_posts_count}</TableCell>
                        <TableCell className="font-medium">{entry.total_posts}</TableCell>
                        <TableCell>{entry.expected_posts}</TableCell>
                        <TableCell className="font-medium">${entry.base_amount?.toFixed(2)}</TableCell>
                        <TableCell>
                          {entry.adjustments_amount > 0 ? (
                            <div className="group relative">
                              <span className="text-green-600 cursor-help underline decoration-dotted">
                                +${entry.adjustments_amount.toFixed(2)}
                              </span>
                              {entryAdjustments.length > 0 && (
                                <div className="absolute left-0 top-full z-50 hidden group-hover:block mt-1 w-64 rounded-md border bg-popover p-3 shadow-lg">
                                  <div className="text-xs font-medium mb-2">Adjustment Details</div>
                                  <div className="space-y-2">
                                    {entryAdjustments.map((adj) => (
                                      <div key={adj.id} className="text-xs border-b border-border pb-2 last:border-0 last:pb-0">
                                        <div className="flex justify-between font-medium">
                                          <span className="capitalize">{adj.adjustment_type === "hour" ? "+1 Hour" : adj.adjustment_type === "day" ? "+1 Day" : `+${adj.post_credit} posts`}</span>
                                          <span className="text-green-600">+${adj.amount.toFixed(2)}</span>
                                        </div>
                                        <div className="text-muted-foreground mt-0.5">
                                          {adj.adjustment_date ? format(new Date(adj.adjustment_date), "MMM d, yyyy") : format(new Date(adj.created_at), "MMM d, yyyy")}
                                        </div>
                                        {adj.notes && (
                                          <div className="text-muted-foreground mt-0.5 italic truncate" title={adj.notes}>
                                            {adj.notes}
                                          </div>
                                        )}
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              )}
                            </div>
                          ) : (
                            "—"
                          )}
                        </TableCell>
                        <TableCell>{getStatusBadge(entry.status)}</TableCell>
                        <TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
                          {entry.status === "pending" && !hasDispute && (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => {
                                setSelectedPayrollId(entry.id);
                                setIsDisputeDialogOpen(true);
                              }}
                            >
                              <AlertCircle className="h-4 w-4 mr-1" />
                              Dispute
                            </Button>
                          )}
                          {hasDispute && (
                            <Badge variant="outline" className="text-yellow-600">Disputed</Badge>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>

        {/* Dispute Dialog */}
        <Dialog open={isDisputeDialogOpen} onOpenChange={setIsDisputeDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Submit Pay Dispute</DialogTitle>
              <DialogDescription>Request a correction to your payroll</DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>Reason</Label>
                <Select
                  value={disputeForm.dispute_reason}
                  onValueChange={(value) => setDisputeForm({ ...disputeForm, dispute_reason: value })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select a reason" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="missing_posts">Missing Posts</SelectItem>
                    <SelectItem value="wrong_date_range">Wrong Date Range</SelectItem>
                    <SelectItem value="adjustment_error">Adjustment Error</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Description</Label>
                <Textarea
                  value={disputeForm.description}
                  onChange={(e) => setDisputeForm({ ...disputeForm, description: e.target.value })}
                  placeholder="Explain the issue..."
                  rows={3}
                />
              </div>
              <div className="space-y-2">
                <Label>Requested Adjustment (optional)</Label>
                <Input
                  type="number"
                  step="0.01"
                  placeholder="$0.00"
                  value={disputeForm.requested_adjustment}
                  onChange={(e) => setDisputeForm({ ...disputeForm, requested_adjustment: e.target.value })}
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setIsDisputeDialogOpen(false)}>
                Cancel
              </Button>
              <Button onClick={handleSubmitDispute} disabled={isSubmitting}>
                {isSubmitting ? "Submitting..." : "Submit Dispute"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    );
  }

  // Admin View
  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-serif text-2xl font-semibold">Payroll</h2>
          <p className="text-muted-foreground">Manage editor compensation dynamically</p>
        </div>
      </div>

      <div className="grid gap-6 md:grid-cols-3">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Pending Payments</CardTitle>
            <Clock className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">${stats.pending.toFixed(2)}</div>
            <p className="text-xs text-muted-foreground">
              {payrollEntries.filter((e) => e.status === "pending").length} pending entries
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Paid This Month</CardTitle>
            <DollarSign className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">${stats.paidThisMonth.toFixed(2)}</div>
            <p className="text-xs text-muted-foreground">
              {new Date().toLocaleString("default", { month: "long" })} payments
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Pending Disputes</CardTitle>
            <AlertCircle className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.pendingDisputes}</div>
            <p className="text-xs text-muted-foreground">Awaiting review</p>
          </CardContent>
        </Card>
      </div>

      {/* Pending Disputes Section */}
      {pendingDisputes.length > 0 && (
        <Card className="border-yellow-200 bg-yellow-50/50">
          <CardHeader>
            <CardTitle className="text-yellow-800">Pending Disputes</CardTitle>
            <CardDescription>Review and resolve editor disputes</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {pendingDisputes.map((dispute) => {
                const entry = payrollEntries.find((e) => e.id === dispute.payroll_id);
                return (
                  <div key={dispute.id} className="flex items-start justify-between p-4 bg-white rounded-lg border">
                    <div>
                      <div className="font-medium">{entry?.user_name || entry?.user_email}</div>
                      <div className="text-sm text-muted-foreground">
                        Reason: {dispute.dispute_reason.replace(/_/g, " ")}
                      </div>
                      {dispute.description && (
                        <p className="text-sm mt-1">{dispute.description}</p>
                      )}
                      {dispute.requested_adjustment && (
                        <p className="text-sm text-green-600">Requested: ${dispute.requested_adjustment}</p>
                      )}
                    </div>
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        className="text-green-600"
                        onClick={() => handleResolveDispute(dispute.id, "approved", "Adjustment approved")}
                      >
                        Approve
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        className="text-red-600"
                        onClick={() => handleResolveDispute(dispute.id, "rejected", "Dispute rejected")}
                      >
                        Reject
                      </Button>
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Editor Rates Section */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Settings className="h-5 w-5" />
                Editor Pay Rates
              </CardTitle>
              <CardDescription>Configure pay rates for each editor</CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Editor</TableHead>
                <TableHead>Rate/Post</TableHead>
                <TableHead>Posts/Hour</TableHead>
                <TableHead>Posts/Day</TableHead>
                <TableHead>Bi-weekly Base</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {editors.map((editor) => {
                const rate = editorRates.find((r) => r.user_id === editor.id);
                return (
                  <TableRow key={editor.id}>
                    <TableCell className="font-medium">{editor.full_name || editor.email}</TableCell>
                    <TableCell>${rate?.rate_per_post?.toFixed(2) || "5.71"}</TableCell>
                    <TableCell>{rate?.posts_per_hour || 2}</TableCell>
                    <TableCell>{rate?.posts_per_day || 14}</TableCell>
                    <TableCell>${rate?.biweekly_base_pay?.toFixed(2) || "800.00"}</TableCell>
                    <TableCell className="text-right">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => openRateDialog(editor.id)}
                      >
                        <Pencil className="h-4 w-4 mr-1" />
                        Edit
                      </Button>
                    </TableCell>
                  </TableRow>
                );
              })}
              {editors.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6} className="text-center text-muted-foreground py-8">
                    No editors found
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <CardTitle>Payroll History</CardTitle>
              <CardDescription>Track editor performance and payments</CardDescription>
            </div>
            <div className="flex gap-2">
              <Select value={filterEditor} onValueChange={setFilterEditor}>
                <SelectTrigger className="w-[180px]">
                  <SelectValue placeholder="Filter by editor" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Editors</SelectItem>
                  {editors.map((editor) => (
                    <SelectItem key={editor.id} value={editor.id}>
                      {editor.full_name || editor.email}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Dialog open={isAddDialogOpen} onOpenChange={setIsAddDialogOpen}>
                <DialogTrigger asChild>
                  <Button>
                    <Plus className="mr-2 h-4 w-4" />
                    Generate Payroll
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Generate Payroll</DialogTitle>
                    <DialogDescription>Auto-calculate pay based on content created</DialogDescription>
                  </DialogHeader>
                  <div className="space-y-4 py-4">
                    <div className="space-y-2">
                      <Label>Editor</Label>
                      <Select
                        value={generateForm.user_id}
                        onValueChange={(value) => setGenerateForm({ ...generateForm, user_id: value })}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select an editor" />
                        </SelectTrigger>
                        <SelectContent>
                          {editors.map((editor) => (
                            <SelectItem key={editor.id} value={editor.id}>
                              {editor.full_name || editor.email}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Period Start</Label>
                        <Input
                          type="date"
                          value={generateForm.period_start}
                          onChange={(e) => setGenerateForm({ ...generateForm, period_start: e.target.value })}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Period End</Label>
                        <Input
                          type="date"
                          value={generateForm.period_end}
                          onChange={(e) => setGenerateForm({ ...generateForm, period_end: e.target.value })}
                        />
                      </div>
                    </div>
                    {generateForm.user_id && (
                      <div className="rounded-lg bg-muted p-3 text-sm">
                        <div className="font-medium">Rate Info</div>
                        <div className="text-muted-foreground">
                          {(() => {
                            const rate = editorRates.find((r) => r.user_id === generateForm.user_id);
                            return rate 
                              ? `$${rate.rate_per_post}/post • ${rate.posts_per_day} posts/day goal`
                              : "Default: $5.71/post • 14 posts/day goal";
                          })()}
                        </div>
                      </div>
                    )}
                  </div>
                  <DialogFooter>
                    <Button variant="outline" onClick={() => setIsAddDialogOpen(false)}>
                      Cancel
                    </Button>
                    <Button onClick={generatePayroll} disabled={isSubmitting}>
                      {isSubmitting ? "Generating..." : "Generate Payroll"}
                    </Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {filteredEntries.length === 0 ? (
            <div className="flex h-48 items-center justify-center rounded-lg border border-dashed border-border">
              <div className="text-center">
                <DollarSign className="mx-auto h-12 w-12 text-muted-foreground/50" />
                <p className="mt-4 text-muted-foreground">No payroll entries found</p>
                <Button
                  variant="outline"
                  className="mt-4"
                  onClick={() => setIsAddDialogOpen(true)}
                >
                  <Plus className="mr-2 h-4 w-4" />
                  Generate First Entry
                </Button>
              </div>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Editor</TableHead>
                  <TableHead>Period</TableHead>
                  <TableHead>Posts</TableHead>
                  <TableHead>Goal</TableHead>
                  <TableHead>Perf.</TableHead>
                  <TableHead>Base</TableHead>
                  <TableHead>Adj.</TableHead>
                  <TableHead>Total</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredEntries.map((entry) => {
                  const entryAdjustments = adjustments.filter((a) => a.payroll_id === entry.id);
                  return (
                    <TableRow 
                      key={entry.id}
                      className="cursor-pointer hover:bg-muted/50"
                      onClick={() => navigate(`/payroll/${entry.id}`)}
                    >
                      <TableCell className="font-medium">
                        {entry.user_name || entry.user_email}
                      </TableCell>
                      <TableCell>
                        {format(new Date(entry.period_start), "MMM d")} - {format(new Date(entry.period_end), "d")}
                      </TableCell>
                      <TableCell>
                        <div className="flex flex-col">
                          <span className="font-medium">{entry.total_posts}</span>
                          <span className="text-xs text-muted-foreground">
                            {entry.galleries_count}g / {entry.blog_posts_count}b
                          </span>
                        </div>
                      </TableCell>
                      <TableCell>{entry.expected_posts}</TableCell>
                      <TableCell>{getPerformanceBadge(entry.performance_percentage)}</TableCell>
                      <TableCell>${entry.base_amount?.toFixed(2)}</TableCell>
                      <TableCell>
                        {entry.adjustments_amount > 0 ? (
                          <div className="group relative">
                            <span className="text-green-600 cursor-help underline decoration-dotted">
                              +${entry.adjustments_amount.toFixed(2)}
                            </span>
                            {entryAdjustments.length > 0 && (
                              <div className="absolute left-0 top-full z-50 hidden group-hover:block mt-1 w-64 rounded-md border bg-popover p-3 shadow-lg">
                                <div className="text-xs font-medium mb-2">Adjustment Details</div>
                                <div className="space-y-2">
                                  {entryAdjustments.map((adj) => (
                                    <div key={adj.id} className="text-xs border-b border-border pb-2 last:border-0 last:pb-0">
                                      <div className="flex justify-between font-medium">
                                        <span className="capitalize">{adj.adjustment_type === "hour" ? "+1 Hour" : adj.adjustment_type === "day" ? "+1 Day" : `+${adj.post_credit} posts`}</span>
                                        <span className="text-green-600">+${adj.amount.toFixed(2)}</span>
                                      </div>
                                      <div className="text-muted-foreground mt-0.5">
                                        {adj.adjustment_date ? format(new Date(adj.adjustment_date), "MMM d, yyyy") : format(new Date(adj.created_at), "MMM d, yyyy")}
                                      </div>
                                      {adj.notes && (
                                        <div className="text-muted-foreground mt-0.5 italic truncate" title={adj.notes}>
                                          {adj.notes}
                                        </div>
                                      )}
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}
                          </div>
                        ) : (
                          "—"
                        )}
                      </TableCell>
                      <TableCell className="font-medium">${entry.amount?.toFixed(2)}</TableCell>
                      <TableCell>{getStatusBadge(entry.status)}</TableCell>
                      <TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
                        <div className="flex justify-end gap-1">
                          {entry.status === "pending" && (
                            <>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => {
                                  setSelectedPayrollId(entry.id);
                                  setIsTimeCreditDialogOpen(true);
                                }}
                                title="Add Time Credit"
                              >
                                <Plus className="h-4 w-4" />
                              </Button>
                              <Button
                                variant="outline"
                                size="sm"
                                className="text-green-600 hover:bg-green-50"
                                onClick={() => handleUpdateStatus(entry.id, "paid")}
                                title="Mark as Paid"
                              >
                                <CheckCircle className="h-4 w-4" />
                              </Button>
                            </>
                          )}
                          <Button
                            variant="outline"
                            size="sm"
                            className="text-destructive hover:bg-destructive hover:text-destructive-foreground"
                            onClick={() => handleDeleteEntry(entry.id)}
                            title="Delete"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Time Credit Dialog */}
      <Dialog open={isTimeCreditDialogOpen} onOpenChange={setIsTimeCreditDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Time Credit</DialogTitle>
            <DialogDescription>Credit additional posts/pay for extra work</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-3 gap-2">
              <Button
                variant={timeCreditForm.adjustment_type === "hour" ? "default" : "outline"}
                onClick={() => setTimeCreditForm({ ...timeCreditForm, adjustment_type: "hour", custom_posts: "" })}
                className="h-20 flex-col"
              >
                <Clock className="h-5 w-5 mb-1" />
                <span className="font-medium">+1 Hour</span>
                <span className="text-xs opacity-80">2 posts</span>
              </Button>
              <Button
                variant={timeCreditForm.adjustment_type === "day" ? "default" : "outline"}
                onClick={() => setTimeCreditForm({ ...timeCreditForm, adjustment_type: "day", custom_posts: "" })}
                className="h-20 flex-col"
              >
                <Calendar className="h-5 w-5 mb-1" />
                <span className="font-medium">+1 Day</span>
                <span className="text-xs opacity-80">14 posts</span>
              </Button>
              <Button
                variant={timeCreditForm.adjustment_type === "custom" ? "default" : "outline"}
                onClick={() => setTimeCreditForm({ ...timeCreditForm, adjustment_type: "custom" })}
                className="h-20 flex-col"
              >
                <TrendingUp className="h-5 w-5 mb-1" />
                <span className="font-medium">Custom</span>
                <span className="text-xs opacity-80">Any amount</span>
              </Button>
            </div>

            {timeCreditForm.adjustment_type === "custom" && (
              <div className="space-y-2">
                <Label>Number of Posts</Label>
                <Input
                  type="number"
                  placeholder="Enter post count"
                  value={timeCreditForm.custom_posts}
                  onChange={(e) => setTimeCreditForm({ ...timeCreditForm, custom_posts: e.target.value })}
                />
              </div>
            )}

            <div className="space-y-2">
              <Label>Date of Work</Label>
              <Input
                type="date"
                value={timeCreditForm.adjustment_date}
                onChange={(e) => setTimeCreditForm({ ...timeCreditForm, adjustment_date: e.target.value })}
              />
            </div>

            <div className="space-y-2">
              <Label>Notes (required)</Label>
              <Textarea
                value={timeCreditForm.notes}
                onChange={(e) => setTimeCreditForm({ ...timeCreditForm, notes: e.target.value })}
                placeholder="Explain why this adjustment is being made..."
                rows={3}
              />
            </div>

            <div className="rounded-lg bg-muted p-3 text-sm">
              <div className="flex justify-between">
                <span>Post Credit:</span>
                <span className="font-medium">
                  {timeCreditForm.adjustment_type === "hour" 
                    ? 2 
                    : timeCreditForm.adjustment_type === "day" 
                      ? 14 
                      : parseInt(timeCreditForm.custom_posts) || 0} posts
                </span>
              </div>
              <div className="flex justify-between mt-1">
                <span>Amount:</span>
                <span className="font-medium text-green-600">
                  +${(
                    (timeCreditForm.adjustment_type === "hour" 
                      ? 2 
                      : timeCreditForm.adjustment_type === "day" 
                        ? 14 
                        : parseInt(timeCreditForm.custom_posts) || 0) * 5.71
                  ).toFixed(2)}
                </span>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsTimeCreditDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleAddTimeCredit} disabled={isSubmitting}>
              {isSubmitting ? "Adding..." : "Add Credit"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Editor Rate Dialog */}
      <Dialog open={isRatesDialogOpen} onOpenChange={setIsRatesDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Pay Rate</DialogTitle>
            <DialogDescription>
              Configure compensation settings for {editors.find((e) => e.id === selectedRateEditor)?.full_name || editors.find((e) => e.id === selectedRateEditor)?.email}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Rate per Post ($)</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={rateForm.rate_per_post}
                  onChange={(e) => setRateForm({ ...rateForm, rate_per_post: e.target.value })}
                />
                <p className="text-xs text-muted-foreground">Amount paid per gallery/blog post</p>
              </div>
              <div className="space-y-2">
                <Label>Bi-weekly Base Pay ($)</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={rateForm.biweekly_base_pay}
                  onChange={(e) => setRateForm({ ...rateForm, biweekly_base_pay: e.target.value })}
                />
                <p className="text-xs text-muted-foreground">Target pay for 2-week period</p>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Posts per Hour Goal</Label>
                <Input
                  type="number"
                  value={rateForm.posts_per_hour}
                  onChange={(e) => setRateForm({ ...rateForm, posts_per_hour: e.target.value })}
                />
                <p className="text-xs text-muted-foreground">Expected hourly output</p>
              </div>
              <div className="space-y-2">
                <Label>Posts per Day Goal</Label>
                <Input
                  type="number"
                  value={rateForm.posts_per_day}
                  onChange={(e) => setRateForm({ ...rateForm, posts_per_day: e.target.value })}
                />
                <p className="text-xs text-muted-foreground">Expected daily output</p>
              </div>
            </div>
            <div className="rounded-lg bg-muted p-3 text-sm">
              <div className="font-medium mb-2">Pay Calculation Preview</div>
              <div className="space-y-1 text-muted-foreground">
                <div className="flex justify-between">
                  <span>If editor hits daily goal for 10 days:</span>
                  <span className="font-medium text-foreground">
                    ${((parseInt(rateForm.posts_per_day) || 14) * 10 * (parseFloat(rateForm.rate_per_post) || 5.71)).toFixed(2)}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span>Expected posts per 2-week period:</span>
                  <span className="font-medium text-foreground">
                    {(parseInt(rateForm.posts_per_day) || 14) * 10} posts
                  </span>
                </div>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsRatesDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleSaveRate} disabled={isSubmitting}>
              {isSubmitting ? "Saving..." : "Save Rate"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Adjustments Detail Modal */}
      <Dialog open={isAdjustmentsDialogOpen} onOpenChange={setIsAdjustmentsDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Time Credit Details</DialogTitle>
            <DialogDescription>
              {(() => {
                const entry = payrollEntries.find((e) => e.id === selectedPayrollId);
                return entry 
                  ? `Pay period: ${format(new Date(entry.period_start), "MMM d")} - ${format(new Date(entry.period_end), "MMM d, yyyy")}`
                  : "";
              })()}
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            {(() => {
              const entryAdjustments = adjustments.filter((a) => a.payroll_id === selectedPayrollId);
              if (entryAdjustments.length === 0) {
                return (
                  <div className="text-center py-8 text-muted-foreground">
                    <Clock className="h-12 w-12 mx-auto mb-3 opacity-50" />
                    <p>No time credits for this pay period</p>
                  </div>
                );
              }
              return (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Date</TableHead>
                      <TableHead>Type</TableHead>
                      <TableHead>Posts</TableHead>
                      <TableHead>Amount</TableHead>
                      <TableHead>Notes</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {entryAdjustments.map((adj) => {
                      const hasAdjDispute = disputes.some(
                        (d) => d.payroll_id === selectedPayrollId && d.description?.includes(`Adjustment ID: ${adj.id}`)
                      );
                      return (
                        <TableRow key={adj.id}>
                          <TableCell className="font-medium">
                            {adj.adjustment_date 
                              ? format(new Date(adj.adjustment_date), "MMM d, yyyy")
                              : format(new Date(adj.created_at), "MMM d, yyyy")}
                          </TableCell>
                          <TableCell>
                            <Badge variant="outline">
                              {adj.adjustment_type === "hour" 
                                ? "+1 Hour" 
                                : adj.adjustment_type === "day" 
                                  ? "+1 Day" 
                                  : "Custom"}
                            </Badge>
                          </TableCell>
                          <TableCell>{adj.post_credit} posts</TableCell>
                          <TableCell className="text-green-600 font-medium">
                            +${adj.amount.toFixed(2)}
                          </TableCell>
                          <TableCell className="max-w-[200px] truncate" title={adj.notes || ""}>
                            {adj.notes || "—"}
                          </TableCell>
                          <TableCell className="text-right">
                            {!hasAdjDispute ? (
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => {
                                  setSelectedAdjustmentId(adj.id);
                                  setAdjustmentDisputeNote("");
                                  setIsAdjustmentDisputeDialogOpen(true);
                                }}
                              >
                                <AlertCircle className="h-4 w-4 mr-1" />
                                Dispute
                              </Button>
                            ) : (
                              <Badge variant="outline" className="text-yellow-600">Disputed</Badge>
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              );
            })()}
            
            {/* Total Summary */}
            {(() => {
              const entryAdjustments = adjustments.filter((a) => a.payroll_id === selectedPayrollId);
              if (entryAdjustments.length === 0) return null;
              
              const totalPosts = entryAdjustments.reduce((sum, adj) => sum + adj.post_credit, 0);
              const totalAmount = entryAdjustments.reduce((sum, adj) => sum + adj.amount, 0);
              
              return (
                <div className="mt-4 rounded-lg bg-muted p-4">
                  <div className="flex justify-between items-center">
                    <div>
                      <span className="text-sm text-muted-foreground">Total Time Credits</span>
                      <p className="font-medium">{entryAdjustments.length} adjustment{entryAdjustments.length !== 1 ? "s" : ""} • {totalPosts} posts</p>
                    </div>
                    <div className="text-right">
                      <span className="text-sm text-muted-foreground">Total Amount</span>
                      <p className="text-xl font-bold text-green-600">+${totalAmount.toFixed(2)}</p>
                    </div>
                  </div>
                </div>
              );
            })()}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsAdjustmentsDialogOpen(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Adjustment Dispute Modal */}
      <Dialog open={isAdjustmentDisputeDialogOpen} onOpenChange={setIsAdjustmentDisputeDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Dispute Time Credit</DialogTitle>
            <DialogDescription>
              Please explain why you're disputing this time credit adjustment
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Dispute Note (required)</Label>
              <Textarea
                value={adjustmentDisputeNote}
                onChange={(e) => setAdjustmentDisputeNote(e.target.value)}
                placeholder="Explain the issue with this time credit..."
                rows={4}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsAdjustmentDisputeDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={async () => {
                if (!adjustmentDisputeNote.trim()) {
                  toast.error("Please provide a note for this dispute");
                  return;
                }
                
                const adj = adjustments.find((a) => a.id === selectedAdjustmentId);
                if (!adj) return;

                setIsSubmitting(true);
                try {
                  const { error } = await supabase.from("payroll_disputes").insert({
                    payroll_id: selectedPayrollId,
                    user_id: user?.id,
                    dispute_reason: "adjustment_error",
                    description: `Adjustment ID: ${adj.id}\nDate: ${adj.adjustment_date || adj.created_at}\nType: ${adj.adjustment_type}\nAmount: $${adj.amount.toFixed(2)}\n\nDispute Note: ${adjustmentDisputeNote}`,
                    status: "pending",
                  });

                  if (error) throw error;

                  toast.success("Dispute submitted successfully");
                  setIsAdjustmentDisputeDialogOpen(false);
                  setAdjustmentDisputeNote("");
                  fetchPayrollData();
                } catch (error: any) {
                  console.error("Error submitting dispute:", error);
                  toast.error(error.message || "Failed to submit dispute");
                } finally {
                  setIsSubmitting(false);
                }
              }}
              disabled={isSubmitting}
            >
              {isSubmitting ? "Submitting..." : "Submit Dispute"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}